import React from 'react';
import { useAppContext } from '../../context/AppContext';
import { Task } from 'shared/types';
import { i18n } from '../../i18n/i18n';
import { showToast } from '../kid/Toast';
import ApproveTasks from '../parent/ApproveTasks';
import { useConfirm } from '../kid/useConfirm';

/** Admin → Pending: approvals plus the "stars not awarded" repair list (split out of AdminPanel.tsx, 2026-10-12). */
const PendingTab: React.FC = () => {
  const { current_user, current_family, verifyTask } = useAppContext() as any;
  const { confirm, dialog: confirmDialog } = useConfirm();

  const handleVerifyTask = async (taskId: string) => {
    try {
      let result = await verifyTask(taskId);
      if (result?.already_verified) {
        if (await confirm({
          title: i18n.t('admin_panel.verify_misattribution_title'),
          message: i18n.t('admin_panel.verify_misattribution_credit_confirm'),
          confirmLabel: i18n.t('admin_panel.verify_misattribution_yes'),
          cancelLabel: i18n.t('settings.cancel'),
        })) {
          result = await verifyTask(taskId, { repairMisattributedPayout: true });
        } else {
          showToast({ message: i18n.t('admin_panel.verify_already_done_hint') });
          return;
        }
      }
      if (result?.misattributed_repair) {
        showToast({ message: i18n.t('admin_panel.verify_misattribution_success') });
      } else if (result?.repaired) {
        showToast({ message: i18n.t('admin_panel.verify_repaired_points_success') });
      } else if (result?.already_repaired_misattribution) {
        showToast({ message: i18n.t('admin_panel.verify_misattribution_already_done') });
      } else if (result?.already_verified) {
        showToast({ message: i18n.t('admin_panel.verify_already_done') });
      } else {
        showToast({ message: i18n.t('admin_panel.verify_success') });
      }
    } catch (error) {
      console.error('Failed to verify task:', error);
      const message = (error as any)?.message || 'Failed to verify task. Please try again.';
      showToast({ message, tone: 'error' });
    }
  };

  if (!current_family || !current_user) return null;
  const tasksNeedingPointsRepair = current_family.tasks.filter((task: Task) => {
    if (!task.verified || !task.completed_by) return false;
    if (task.points_awarded_on_verification || task.misattributed_payout_repair_at) return false;
    return true;
  });
  return (
    <>
    <div className="mb-8">
      <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark mb-4">{i18n.t('admin_panel.pending_verification')}</h3>
      {tasksNeedingPointsRepair.length > 0 && (
        <div className="mb-8 rounded-kid-card border-2 border-amber-200 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-950/30 p-4">
          <h4 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-1">
            {i18n.t('admin_panel.points_not_awarded_section')}
          </h4>
          <p className="text-sm text-amber-800/90 dark:text-amber-200/90 mb-4">{i18n.t('admin_panel.points_not_awarded_help')}</p>
          <div className="space-y-3">
            {tasksNeedingPointsRepair.map((task: any) => {
              const completer = current_family.members.find((m: any) => m.id === (task as any).completed_by);
              return (
                <div
                  key={task.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white/80 dark:bg-gray-900/50 rounded-kid-tile border-2 border-amber-100 dark:border-amber-900/40 p-3"
                >
                  <div>
                    <div className="font-medium text-kid-text dark:text-kid-text-dark">{task.title}</div>
                    <div className="text-sm text-kid-text dark:text-kid-text-muted-dark">
                      {i18n.t('task_details.completed_by')}: {completer?.name || i18n.t('rewards.unknown_member')} · ⭐ {task.points}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 shrink-0"
                    onClick={() => handleVerifyTask(task.id)}
                  >
                    {i18n.t('admin_panel.award_missing_points')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ApproveTasks embedded />
    </div>
      {confirmDialog}
    </>
  );
};

export default PendingTab;
